import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { byStatus, finApi, finErr, isArchived } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import { LINE_BUCKETS } from "@/components/nds/windows/finance/InvoiceLinesTab";

const today = () => new Date().toISOString().slice(0, 10);

export default function InvoiceForm({ onDone, onCancel, vendorId, prefill }) {
  const [vendors, setVendors] = useState([]);
  const [residents, setResidents] = useState([]);
  const [q, setQ] = useState("");
  const { line: preLine, ...preF } = prefill || {};
  const [f, setF] = useState({ vendor_id: vendorId || "", invoice_number: "", invoice_date: today(), period_start: today(), period_end: today(), self_reported: false, resident_ids: [], ...preF });
  const [line, setLine] = useState(preLine || { description: "", quantity: "1", unit_amount: "", bucket: "escrow" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    finApi.get("/vendors", { active: true }).then((d) => setVendors(d.vendors)).catch(() => {});
    finApi.get("/residents", { status: "all" }).then((d) => setResidents(byStatus(d.residents))).catch(() => {});
  }, []);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleRes = (id) => setF((x) => ({ ...x, resident_ids: x.resident_ids.includes(id) ? x.resident_ids.filter((r) => r !== id) : [...x.resident_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const lines = line.description && line.unit_amount ? [{ ...line, quantity: Number(line.quantity), unit_amount: Number(line.unit_amount) }] : [];
      const inv = await finApi.post("/invoices", { ...f, lines });
      toast.success(`Invoice created · pays ${inv.pay_date}`);
      onDone(inv.id);
    } catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  const L = ({ label, children }) => <label className="space-y-1 block"><span className="nds-label-dim text-[9px]">{label}</span>{children}</label>;
  const picked = residents.filter((r) => f.resident_ids.includes(r.id));
  const shown = [...picked, ...residents.filter((r) => !f.resident_ids.includes(r.id) && (q ? `${r.first_name} ${r.last_name}`.toLowerCase().includes(q.toLowerCase()) : !isArchived(r)))].slice(0, 60);

  return (
    <form onSubmit={submit} data-testid="fin-inv-form" className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {L({ label: "Vendor", children: (
          <select required value={f.vendor_id} onChange={set("vendor_id")} data-testid="fin-inv-form-vendor" className={`${field} w-full`}>
            <option value="">Select vendor…</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.type}</option>)}
          </select>) })}
        {L({ label: "Invoice # (auto if blank)", children: <input value={f.invoice_number} onChange={set("invoice_number")} data-testid="fin-inv-form-number" className={`${field} w-full`} /> })}
        {L({ label: "Invoice date", children: <input type="date" required value={f.invoice_date} onChange={set("invoice_date")} data-testid="fin-inv-form-date" className={`${field} w-full`} /> })}
        {L({ label: "Period start", children: <input type="date" required value={f.period_start} onChange={set("period_start")} data-testid="fin-inv-form-start" className={`${field} w-full`} /> })}
        {L({ label: "Period end", children: <input type="date" required value={f.period_end} onChange={set("period_end")} data-testid="fin-inv-form-end" className={`${field} w-full`} /> })}
        <label className="flex items-center gap-2 pt-5 text-sm text-nex-silver">
          <input type="checkbox" checked={f.self_reported} onChange={set("self_reported")} data-testid="fin-inv-form-self-reported" className="accent-[#d4af37]" /> Self-reported
        </label>
      </div>
      <div className="space-y-1">
        <span className="nds-label-dim text-[9px]">Linked residents (optional) · {f.resident_ids.length} selected</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents (includes archived)…" data-testid="fin-inv-form-res-search" className={`${field} w-full !py-1.5`} />
        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
          {shown.map((r) => {
            const on = f.resident_ids.includes(r.id);
            return (
              <button key={r.id} type="button" onClick={() => toggleRes(r.id)} data-testid={`fin-inv-form-res-${r.id}`} aria-pressed={on}
                className={`px-2 py-0.5 rounded-full border text-[11px] transition-colors ${on ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>
                {r.first_name} {r.last_name}{isArchived(r) && <span className="ml-1 text-[9px] uppercase tracking-wider opacity-70">Archived</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_80px_120px_140px] gap-2 [&>*:first-child]:col-span-2 sm:[&>*:first-child]:col-span-1">
        <input value={line.description} onChange={(e) => setLine((x) => ({ ...x, description: e.target.value }))} placeholder="First line (optional)" data-testid="fin-inv-form-line-desc" className={field} />
        <input type="number" min="0.01" step="0.01" value={line.quantity} onChange={(e) => setLine((x) => ({ ...x, quantity: e.target.value }))} data-testid="fin-inv-form-line-qty" className={field} />
        <input type="number" min="0" step="0.01" value={line.unit_amount} onChange={(e) => setLine((x) => ({ ...x, unit_amount: e.target.value }))} placeholder="Unit $" data-testid="fin-inv-form-line-unit" className={field} />
        <select value={line.bucket} onChange={(e) => setLine((x) => ({ ...x, bucket: e.target.value }))} data-testid="fin-inv-form-line-bucket" className={field}>
          {LINE_BUCKETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-nex-muted">Pay date is calculated by the server from the period end.</span>
        <button type="button" onClick={onCancel} data-testid="fin-inv-form-cancel" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !f.vendor_id} data-testid="fin-inv-form-submit" className={btnGold}>Create draft</button>
      </div>
    </form>
  );
}
